import { API_CONFIG } from "../config/environment";

const createAuthHeaders = (token, additionalHeaders = {}) => {
  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...additionalHeaders,
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
};

const handleResponse = async (response) => {
  if (response.status === 401 || response.status === 403) {
    console.warn("🔐 Token expirado o no autorizado en API");
    window.dispatchEvent(
      new CustomEvent("token-expired", {
        detail: { status: response.status },
      })
    );

    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error?.message || "Token expirado o no autorizado"
    );
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error?.message || `HTTP error! status: ${response.status}`
    );
  }

  return response.json();
};

export const directSalesService = {
  // Obtener todas las ventas directas
  getDirectSales: async (params = {}, token) => {
    if (!token) {
      throw new Error("Token requerido para acceder a ventas directas");
    }

    try {
      const queryParams = new URLSearchParams();

      Object.entries(params).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== "") {
          queryParams.append(key, value.toString());
        }
      });

      const url = `${API_CONFIG.V2_BASE_URL}/bienes/direct-sales?${queryParams}`;
      console.log(`🔍 Fetching direct sales:`, url);

      const response = await fetch(url, {
        method: "GET",
        headers: createAuthHeaders(token),
      });

      const data = await handleResponse(response);
      console.log(`✅ Direct sales fetched:`, data);

      return data;
    } catch (error) {
      console.error("❌ Error fetching direct sales:", error);
      throw error;
    }
  },

  // Obtener una venta directa por ID
  getDirectSaleById: async (saleId, token) => {
    if (!token) {
      throw new Error("Token requerido");
    }

    try {
      const url = `${API_CONFIG.V2_BASE_URL}/bienes/direct-sales/${saleId}`;
      console.log(`🔍 Fetching direct sale ${saleId}`);

      const response = await fetch(url, {
        method: "GET",
        headers: createAuthHeaders(token),
      });

      const data = await handleResponse(response);
      return data;
    } catch (error) {
      console.error(`❌ Error fetching direct sale ${saleId}:`, error);
      throw error;
    }
  },

  // Cancelar una venta directa
  cancelDirectSale: async (saleId, reason, token) => {
    if (!token) {
      throw new Error("Token requerido para cancelar ventas");
    }

    try {
      const url = `${API_CONFIG.V2_BASE_URL}/bienes/direct-sales/${saleId}`;
      console.log(`🗑️ Cancelling direct sale ${saleId}`);

      const response = await fetch(url, {
        method: "DELETE",
        headers: createAuthHeaders(token),
        body: JSON.stringify({
          cancellationReason: reason,
        }),
      });

      const data = await handleResponse(response);
      console.log(`✅ Direct sale cancelled:`, data);

      return data;
    } catch (error) {
      console.error(`❌ Error cancelling direct sale ${saleId}:`, error);
      throw error;
    }
  },

  // Crear una venta directa (ya existe en DirectSaleModal, pero por consistencia)
  createDirectSale: async (saleData, token) => {
    if (!token) {
      throw new Error("Token requerido");
    }

    try {
      const url = `${API_CONFIG.V2_BASE_URL}/bienes/direct-sales`;
      console.log(`📝 Creating direct sale`);

      const response = await fetch(url, {
        method: "POST",
        headers: createAuthHeaders(token),
        body: JSON.stringify(saleData),
      });

      const data = await handleResponse(response);
      console.log(`✅ Direct sale created:`, data);

      return data;
    } catch (error) {
      console.error("❌ Error creating direct sale:", error);
      throw error;
    }
  },

  // Verificar si un lote está vendido
  checkLotSold: async (lotId, token) => {
    if (!token) {
      throw new Error("Token requerido");
    }

    try {
      const url = `${API_CONFIG.V2_BASE_URL}/bienes/direct-sales/lot/${lotId}`;
      console.log(`🔍 Checking if lot ${lotId} is sold`);

      const response = await fetch(url, {
        method: "GET",
        headers: createAuthHeaders(token),
      });

      const data = await handleResponse(response);
      console.log(`✅ Lot ${lotId} sold status:`, data);

      return data;
    } catch (error) {
      console.error(`❌ Error checking lot ${lotId}:`, error);
      throw error;
    }
  },

  // Actualizar estado de pago de una venta
  updatePaymentStatus: async (saleId, paymentStatus, token) => {
    if (!token) {
      throw new Error("Token requerido");
    }

    try {
      const url = `${API_CONFIG.V2_BASE_URL}/bienes/direct-sales/${saleId}/payment-status`;
      console.log(
        `💰 Updating payment status for sale ${saleId} to ${paymentStatus}`
      );

      const response = await fetch(url, {
        method: "PUT",
        headers: createAuthHeaders(token),
        body: JSON.stringify({ paymentStatus }),
      });

      const data = await handleResponse(response);
      console.log(`✅ Payment status updated:`, data);

      return data;
    } catch (error) {
      console.error(
        `❌ Error updating payment status for sale ${saleId}:`,
        error
      );
      throw error;
    }
  },

  // Obtener estadísticas de ventas
  getStats: async (params = {}, token) => {
    if (!token) {
      throw new Error("Token requerido");
    }

    try {
      const queryParams = new URLSearchParams();

      Object.entries(params).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== "") {
          queryParams.append(key, value.toString());
        }
      });

      const url = `${API_CONFIG.V2_BASE_URL}/bienes/direct-sales/stats?${queryParams}`;
      console.log(`📊 Fetching direct sales stats`);

      const response = await fetch(url, {
        method: "GET",
        headers: createAuthHeaders(token),
      });

      const data = await handleResponse(response);
      console.log(`✅ Stats fetched:`, data);

      return data;
    } catch (error) {
      console.error("❌ Error fetching stats:", error);
      throw error;
    }
  },

  // Reactivar una venta directa cancelada
  reactivateDirectSale: async (saleId, token) => {
    if (!token) {
      throw new Error("Token requerido para reactivar ventas");
    }

    try {
      const url = `${API_CONFIG.V2_BASE_URL}/bienes/direct-sales/${saleId}/reactivate`;
      console.log(`🔄 Reactivating direct sale ${saleId}`);

      const response = await fetch(url, {
        method: "PATCH",
        headers: createAuthHeaders(token),
      });

      const data = await handleResponse(response);
      console.log(`✅ Direct sale reactivated:`, data);

      return data;
    } catch (error) {
      console.error(`❌ Error reactivating direct sale ${saleId}:`, error);
      throw error;
    }
  },

  // Obtener historial de auditoría de una venta directa
  getAuditHistory: async (saleId, token) => {
    if (!token) {
      throw new Error("Token requerido para ver historial de auditoría");
    }

    try {
      const url = `${API_CONFIG.V2_BASE_URL}/bienes/direct-sales/${saleId}/audit-history`;
      console.log(`📜 Fetching audit history for sale ${saleId}`);

      const response = await fetch(url, {
        method: "GET",
        headers: createAuthHeaders(token),
      });

      const data = await handleResponse(response);
      console.log(`✅ Audit history fetched:`, data);

      return data;
    } catch (error) {
      console.error(
        `❌ Error fetching audit history for sale ${saleId}:`,
        error
      );
      throw error;
    }
  },

  // Actualizar una venta directa existente
  updateDirectSale: async (saleId, updateData, token) => {
    if (!token) {
      throw new Error("Token requerido para actualizar ventas");
    }

    try {
      const url = `${API_CONFIG.V2_BASE_URL}/bienes/direct-sales/${saleId}`;
      console.log(`📝 Updating direct sale ${saleId}`);

      const response = await fetch(url, {
        method: "PUT",
        headers: createAuthHeaders(token),
        body: JSON.stringify(updateData),
      });

      const data = await handleResponse(response);
      console.log(`✅ Direct sale updated:`, data);

      return data;
    } catch (error) {
      console.error(`❌ Error updating direct sale ${saleId}:`, error);
      throw error;
    }
  },
};

export default directSalesService;
